'use es6';

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MoveableObject } from 'foundations-components/transitional/components/MoveableObject';
import { EditIcon, DeleteIcon } from 'foundations-assets';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.form.withInlineEditing.${key}`;
const FormField = ({
  label = '',
  isDeletable = true,
  draggable = false,
  hasFormsWriteAccess = true,
  DragHandle = () => {},
  onEditClick = () => {},
  onRemoveClick = () => {},
  readOnly = false
}) => /*#__PURE__*/_jsxs(TFlex, {
  "data-selenium": "form-field",
  children: [/*#__PURE__*/_jsx(Box, {
    grow: 1,
    children: draggable ?
    /*#__PURE__*/
    // This is a hack to make the ui-addon-draggable@1 DragHandle/UISortableList
    // look/work OK with the foundations MoveableObject which is better used
    // with @ui-addon-sortable@3
    _jsx(MoveableObject, {
      closeable: false,
      draggable: false,
      padding: "0",
      children: /*#__PURE__*/_jsx(DragHandle, {
        children: /*#__PURE__*/_jsx(View, {
          as: "span",
          display: "inline-block",
          blockSize: "40px",
          inlineSize: "100%",
          paddingInlineStart: "600",
          paddingInlineEnd: "700",
          paddingBlock: "200",
          children: /*#__PURE__*/_jsx(Flex, {
            children: /*#__PURE__*/_jsx(Text, {
              as: "span",
              truncate: true,
              children: label
            })
          })
        })
      })
    }) : /*#__PURE__*/_jsx(MoveableObject, {
      closeable: false,
      draggable: false,
      children: label
    })
  }), /*#__PURE__*/_jsxs(Box, {
    shrink: 0,
    paddingInlineStart: "300",
    children: [hasFormsWriteAccess && /*#__PURE__*/_jsx(IconButton, {
      "data-selenium": "form-field-edit-button",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('formField.edit')
      }),
      placement: "top",
      onClick: onEditClick,
      disabled: readOnly,
      children: /*#__PURE__*/_jsx(EditIcon, {})
    }), isDeletable && hasFormsWriteAccess && /*#__PURE__*/_jsx(IconButton, {
      "data-selenium": "form-field-remove-button",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('formField.delete')
      }),
      placement: "top",
      onClick: onRemoveClick,
      disabled: readOnly,
      children: /*#__PURE__*/_jsx(DeleteIcon, {})
    })]
  })]
});
const FormFieldsWithoutReordering = ({
  fields = [],
  hasFormsWriteAccess = false,
  onEditClick = () => {},
  onRemoveClick = () => {},
  readOnly = false
}) => /*#__PURE__*/_jsx("div", {
  children: fields.map(({
    field
  }, key) => /*#__PURE__*/_jsx(View, {
    marginBlockEnd: "200",
    as: "div",
    children: /*#__PURE__*/_jsx(FormField, {
      label: field.label || field.placeholder,
      draggable: false,
      isDeletable: field.name !== 'email',
      onEditClick: () => onEditClick(key),
      onRemoveClick: () => onRemoveClick(key),
      hasFormsWriteAccess: hasFormsWriteAccess,
      readOnly: readOnly
    }, key)
  }, key)).valueSeq().toArray()
});
const FormFieldsWithReordering = props => {
  const {
    fields = [],
    onOrderChange = () => {},
    onEditClick = () => {},
    onRemoveClick = () => {},
    DraggableComponents
  } = props;
  const {
    UISortableList,
    UIDragListItem,
    UIDragHandle
  } = DraggableComponents;
  const [_order, setFieldsOrder] = useState([...fields.keys()]);
  const [_fields, setFormFields] = useState(fields);
  useEffect(() => {
    setFormFields(props.fields);
    if (_order.length !== props.fields.size) {
      setFieldsOrder([...props.fields.keys()]);
    }
  }, [_order.length, props.fields]);
  const updateOrder = updatedOrder => {
    setFieldsOrder(updatedOrder);
    onOrderChange(updatedOrder);
  };
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UISortableList, {
      dragGroup: "unique-key",
      order: _order,
      onOrderChange: updateOrder,
      children: _fields.map(({
        field
      }, key) => /*#__PURE__*/_jsx(UIDragListItem, {
        dragId: key,
        children: /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(FormField, {
            label: field.label || field.placeholder,
            draggable: true,
            DragHandle: UIDragHandle,
            isDeletable: field.name !== 'email',
            onEditClick: () => onEditClick(key),
            onRemoveClick: () => onRemoveClick(key)
          }, key)
        })
      }, key)).valueSeq().toArray()
    })
  });
};
const getFormFieldsComponent = ({
  canChangeFieldsOrder,
  DraggableComponents,
  hasFormsWriteAccess,
  readOnly
}) => {
  return !readOnly && canChangeFieldsOrder && DraggableComponents && DraggableComponents.UISortableList && DraggableComponents.UIDragListItem && DraggableComponents.UIDragHandle && hasFormsWriteAccess ? FormFieldsWithReordering : FormFieldsWithoutReordering;
};
const propTypes = {
  fields: PropTypes.instanceOf(OrderedMap),
  onEditClick: PropTypes.func,
  onRemoveClick: PropTypes.func
};
FormFieldsWithReordering.propTypes = Object.assign({}, propTypes, {
  DraggableComponents: PropTypes.object.isRequired,
  onOrderChange: PropTypes.func
});
FormFieldsWithoutReordering.propTypes = Object.assign({}, propTypes, {
  hasFormsWriteAccess: PropTypes.bool
});
export default getFormFieldsComponent;